import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { recordPath } from '../routes'
import { useAuthStore } from '../store/authStore'
import { useTerminalTheme } from '../theme/terminalTheme'
import AppFooter from './AppFooter'
import Logo from './Logo'
import { SurfaceThemeProvider } from '../theme/surfaceTheme'

/**
 * The frame for public record pages (/r/<id>, /verify): the signed-in pages'
 * terminal palette, so a call looks the same to everyone, and the app footer
 * with its risk line, without the sidebar. Always dark, whatever the signed-in
 * user's theme choice: these pages are shared and have no theme control. Signed out, "Track record" goes to
 * the fee calculator (recordPath) and is hidden on that page itself.
 */
const PublicShell = ({ children }: { children: ReactNode }) => {
  useTerminalTheme()
  const signedIn = useAuthStore((state) => Boolean(state.token))
  const record = recordPath(signedIn)
  const { pathname } = useLocation()
  return (
    <SurfaceThemeProvider value="dark">
    <div className="min-h-screen bg-gray-50 dark:bg-terminal-bg dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-terminal-border dark:bg-terminal-surface">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" aria-label="Home">
            <Logo size="md" />
          </Link>
          <nav className="flex items-center gap-4">
            {pathname !== '/learn' && (
              <Link to="/learn" className="text-sm text-gray-300 hover:text-gray-100" data-testid="shell-courses-link">
                Courses
              </Link>
            )}
            {pathname !== record && (
              <Link to={record} className="text-sm text-gray-300 hover:text-gray-100" data-testid="shell-record-link">
                Track record
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-6">
        {children}
        <AppFooter />
      </main>
    </div>
    </SurfaceThemeProvider>
  )
}

export default PublicShell
